import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingSection = () => {
  const [selectedSeats, setSelectedSeats] = useState(1);

  const pricingTiers = [
    {
    id: 1,
    name: "Single-Seat License",
    price: "65,000",
    currency: "NGN",
    period: "Monthly payment",
      description: "Perfect for single-store operations with one POS terminal",
      features: [
        "1 POS Terminal License",
        "Unlimited Transactions",
        "Local Data Storage",
        "Receipt Printing",
        "Barcode Scanning",
        "Inventory Management",
        "Sales Reporting",
        "Customer Management",
        "Free Software Updates",
        "Email Support"
      ],
      highlighted: false,
      cta: "Purchase License",
      icon: "Monitor"
    },
    {
      id: 2,
      name: "Multi-Seat License",
      price: "Contact Sales",
      currency: "",
      period: "Custom pricing",
      description: "Ideal for stores with multiple POS terminals and staff members",
      features: [
        "2-10 POS Terminal Licenses",
        "Unlimited Transactions",
        "Centralized Data Management",
        "All Single-Seat Features",
        "Multi-User Access Control",
        "Advanced Reporting",
        "Priority Email Support",
        "Phone Support",
        "Free Software Updates",
        "Volume Discount Available"
      ],
      highlighted: true,
      cta: "Contact Sales Team",
      icon: "Laptop"
    },
    {
      id: 3,
      name: "Enterprise License",
      price: "Custom Quote",
      currency: "",
      period: "Tailored solution",
      description: "Comprehensive solution for retail chains and large operations",
      features: [
        "Unlimited POS Terminals",
        "Multi-Store Management",
        "Custom Integration Support",
        "All Multi-Seat Features",
        "Dedicated Account Manager",
        "On-Site Training",
        "24/7 Priority Support",
        "Custom Feature Development",
        "API Access",
        "White-Label Options"
      ],
      highlighted: false,
      cta: "Request Custom Quote",
      icon: "Building"
    }
  ];

  const calculatePrice = (seats) => {
    if (seats === 1) return "65,000";
    if (seats <= 5) return `${(seats * 55000)?.toLocaleString()}`;
    if (seats <= 10) return `${(seats * 50000)?.toLocaleString()}`;
    return "Contact Sales";
  };

  return (
    <section className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 md:mb-6">
            Desktop POS Pricing
          </h2>
          <p className="font-body text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto mb-8 md:mb-10">
            Flexible monthly billing for Desktop licenses with offline-first reliability and local data ownership. Contact sales for exact monthly pricing and volume discounts.
          </p>

          <div className="inline-flex items-center justify-center px-4 md:px-6 py-3 md:py-4 bg-warning/10 rounded-xl border border-warning/20">
            <Icon name="TrendingDown" size={24} color="var(--color-warning)" strokeWidth={2} className="mr-3" />
            <span className="font-body text-sm md:text-base font-medium text-warning">
              Save up to 30% with multi-seat licenses
            </span>
          </div>
        </div>

        <div className="max-w-5xl mx-auto mb-12 md:mb-16">
          <div className="bg-card rounded-xl border border-border shadow-purple-md p-6 md:p-8">
            <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground mb-4 md:mb-6 text-center">
              Pricing Calculator
            </h3>
            
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 md:gap-8">
              <div className="flex-1 w-full">
                <label className="font-body text-sm md:text-base font-medium text-foreground mb-3 block">
                  Number of POS Terminals
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={selectedSeats}
                  onChange={(e) => setSelectedSeats(parseInt(e?.target?.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <div className="flex justify-between mt-2">
                  <span className="font-body text-xs md:text-sm text-muted-foreground">1</span>
                  <span className="font-body text-xs md:text-sm text-muted-foreground">5</span>
                  <span className="font-body text-xs md:text-sm text-muted-foreground">10+</span>
                </div>
              </div>

              <div className="flex-shrink-0 text-center">
                <div className="font-heading text-4xl md:text-5xl font-bold text-primary mb-2">
                  {selectedSeats}
                </div>
                <div className="font-body text-sm md:text-base text-muted-foreground">
                  {selectedSeats === 1 ? 'Terminal' : 'Terminals'}
                </div>
              </div>

              <div className="flex-shrink-0 text-center p-4 md:p-6 bg-primary/10 rounded-xl border border-primary/20">
                <div className="font-body text-xs md:text-sm text-muted-foreground mb-1">Estimated Price</div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-primary">
                  {selectedSeats <= 10 ? `NGN ${calculatePrice(selectedSeats)}` : 'Custom Quote'}
                </div>
                <div className="font-body text-xs md:text-sm text-muted-foreground mt-1">Estimated monthly price</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto">
          {pricingTiers?.map((tier) => (
            <div
              key={tier?.id}
              className={`bg-card rounded-xl border transition-smooth hover:scale-105 ${
                tier?.highlighted
                  ? 'border-primary shadow-purple-lg'
                  : 'border-border shadow-purple-sm hover:shadow-purple-md'
              } overflow-hidden`}
            >
              {tier?.highlighted && (
                <div className="bg-primary text-primary-foreground text-center py-2 md:py-3">
                  <span className="font-body text-xs md:text-sm font-medium">MOST POPULAR</span>
                </div>
              )}

              <div className="p-6 md:p-8">
                <div className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 bg-primary/10 rounded-lg mb-4 md:mb-6 mx-auto">
                  <Icon name={tier?.icon} size={24} color="var(--color-primary)" strokeWidth={2} />
                </div>

                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground text-center mb-2">
                  {tier?.name}
                </h3>

                <p className="font-body text-sm md:text-base text-muted-foreground text-center mb-6 md:mb-8">
                  {tier?.description}
                </p>

                <div className="text-center mb-6 md:mb-8">
                  {tier?.currency && (
                    <div className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-2">
                      {tier?.currency} {tier?.price}
                    </div>
                  )}
                  {!tier?.currency && (
                    <div className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
                      {tier?.price}
                    </div>
                  )}
                  <div className="font-body text-sm md:text-base text-muted-foreground">
                    {tier?.period}
                  </div>
                </div>

                <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                  {tier?.features?.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Icon 
                        name="CheckCircle" 
                        size={20} 
                        color="var(--color-success)" 
                        strokeWidth={2}
                        className="flex-shrink-0 mt-0.5"
                      />
                      <span className="font-body text-sm md:text-base text-foreground">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant={tier?.highlighted ? "default" : "outline"}
                  size="lg"
                  fullWidth
                  iconName={tier?.id === 1 ? "Download" : "Phone"}
                  iconPosition="left"
                >
                  {tier?.cta}
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 text-center">
          <p className="font-body text-sm md:text-base text-muted-foreground mb-4 md:mb-6">
            All licenses include free software updates and technical support
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            <div className="flex items-center gap-2">
              <Icon name="Shield" size={20} color="var(--color-success)" strokeWidth={2} />
              <span className="font-body text-sm md:text-base text-foreground">Secure Payment</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="RefreshCw" size={20} color="var(--color-success)" strokeWidth={2} />
              <span className="font-body text-sm md:text-base text-foreground">30-Day Money Back</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Headphones" size={20} color="var(--color-success)" strokeWidth={2} />
              <span className="font-body text-sm md:text-base text-foreground">Lifetime Support</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;